import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Users, BookOpen, Headphones, Download, TrendingUp, Library, Clock, BarChart3 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { useQuery } from "@tanstack/react-query";
import { isSupabaseConfigured, supabase } from "@/lib/supabase";

interface MetricsData {
  users: Array<{ id: string; email: string; joined_at: string; last_active_at: string | null }>;
  library: Array<{ user_id: string; content_item_id: string; added_at: string }>;
  progress: Array<{ user_id: string; content_item_id: string; total_progress_percent: number; total_listened_seconds: number; last_opened_at: string }>;
  downloads: Array<{ user_id: string; content_id: string; action: string; created_at: string }>;
  content: Array<{ id: string; title: string; status: string; content_type: string }>;
  events: Array<{ event_name: string; properties: any; created_at: string; user_id: string | null }>;
}

async function fetchMetrics(): Promise<MetricsData> {
  if (!isSupabaseConfigured || !supabase) {
    return { users: [], library: [], progress: [], downloads: [], content: [], events: [] };
  }
  const [usersRes, libRes, progRes, dlRes, contentRes, eventsRes] = await Promise.all([
    supabase.from("app_users").select("id,email,joined_at,last_active_at"),
    supabase.from("user_library").select("user_id,content_item_id,added_at"),
    supabase.from("user_progress").select("user_id,content_item_id,total_progress_percent,total_listened_seconds,last_opened_at"),
    supabase.from("download_events").select("user_id,content_id,action,created_at"),
    supabase.from("content_items").select("id,title,status,content_type"),
    supabase.from("analytics_events").select("event_name,properties,created_at,user_id").order("created_at", { ascending: false }).limit(500),
  ]);
  return {
    users: (usersRes.data || []) as any[],
    library: (libRes.data || []) as any[],
    progress: (progRes.data || []) as any[],
    downloads: (dlRes.data || []) as any[],
    content: (contentRes.data || []) as any[],
    events: (eventsRes.data || []) as any[],
  };
}

function daysAgo(dateStr: string | null): number {
  if (!dateStr) return Infinity;
  return Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
}

function formatSeconds(s: number): string {
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  return `${(s / 3600).toFixed(1)}h`;
}

export default function MetricsPage() {
  const { data, isLoading } = useQuery({
    queryKey: ["metrics-data"],
    queryFn: fetchMetrics,
  });

  const kpis = useMemo(() => {
    if (!data) return null;

    const totalUsers = data.users.length;
    const activeUsers7d = data.users.filter((u) => daysAgo(u.last_active_at) <= 7).length;
    const activeUsers30d = data.users.filter((u) => daysAgo(u.last_active_at) <= 30).length;
    const newUsersThisWeek = data.users.filter((u) => daysAgo(u.joined_at) <= 7).length;

    const usersWithLibrary = new Set(data.library.map((l) => l.user_id)).size;
    const conversionRate = totalUsers > 0 ? Math.round((usersWithLibrary / totalUsers) * 100) : 0;
    const totalLibraryAdds = data.library.length;

    const usersWithProgress = new Set(data.progress.map((p) => p.user_id)).size;
    const totalListenedSeconds = data.progress.reduce((sum, p) => sum + (p.total_listened_seconds || 0), 0);
    const avgProgress = data.progress.length > 0
      ? Math.round(data.progress.reduce((sum, p) => sum + (p.total_progress_percent || 0), 0) / data.progress.length)
      : 0;

    const uniqueDownloaders = new Set(data.downloads.filter((d) => d.action === "download").map((d) => d.user_id)).size;
    const totalDownloads = data.downloads.filter((d) => d.action === "download").length;
    const offlineAdoption = totalUsers > 0 ? Math.round((uniqueDownloaders / totalUsers) * 100) : 0;

    const publishedContent = data.content.filter((c) => c.status === "published").length;
    const draftContent = data.content.filter((c) => c.status === "draft").length;
    const totalContent = data.content.length;

    // Top content by library adds
    const contentAddCounts = new Map<string, number>();
    for (const l of data.library) {
      contentAddCounts.set(l.content_item_id, (contentAddCounts.get(l.content_item_id) || 0) + 1);
    }
    const topContent = [...contentAddCounts.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([id, count]) => ({
        title: data.content.find((c) => c.id === id)?.title || id,
        adds: count,
      }));

    // Registrations by day (last 30 days)
    const registrationsByDay = new Map<string, number>();
    for (const u of data.users) {
      const day = u.joined_at?.slice(0, 10);
      if (day && daysAgo(u.joined_at) <= 30) {
        registrationsByDay.set(day, (registrationsByDay.get(day) || 0) + 1);
      }
    }
    const regChart = [...registrationsByDay.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([date, count]) => ({ date: date.slice(5), count }));

      // Event analytics
      const eventCounts = new Map<string, number>();
      for (const ev of data.events) {
        eventCounts.set(ev.event_name, (eventCounts.get(ev.event_name) || 0) + 1);
      }
      const eventChart = [...eventCounts.entries()]
        .sort((a, b) => b[1] - a[1])
        .map(([name, count]) => ({ name, count }));

      const recentEvents = data.events.slice(0, 15);

      // Funnel: Paywall shown → Purchase start → Success
      const paywallShown = eventCounts.get("paywall_shown") || 0;
      const purchaseStart = eventCounts.get("paywall_purchase_start") || 0;
      const purchaseSuccess = eventCounts.get("paywall_purchase_success") || 0;
      const startRate = paywallShown > 0 ? Math.round((purchaseStart / paywallShown) * 100) : 0;
      const completeRate = purchaseStart > 0 ? Math.round((purchaseSuccess / purchaseStart) * 100) : 0;
      const overallConvRate = paywallShown > 0 ? Math.round((purchaseSuccess / paywallShown) * 100) : 0;

      // Funnel: Preview → Purchase (desde reader preview)
      const previewsOpened = eventCounts.get("open_preview") || 0;
      const previewToPurchase = eventCounts.get("reader_preview_to_purchase") || 0;
      const previewConvRate = previewsOpened > 0 ? Math.round((previewToPurchase / previewsOpened) * 100) : 0;

      return {
        totalUsers, activeUsers7d, activeUsers30d, newUsersThisWeek,
        usersWithLibrary, conversionRate, totalLibraryAdds,
        usersWithProgress, totalListenedSeconds, avgProgress,
        uniqueDownloaders, totalDownloads, offlineAdoption,
        publishedContent, draftContent, totalContent,
        topContent, regChart, eventChart, recentEvents,
        totalEvents: data.events.length,
        paywallShown, purchaseStart, purchaseSuccess,
        startRate, completeRate, overallConvRate,
        previewsOpened, previewToPurchase, previewConvRate,
      };
  }, [data]);

  if (isLoading || !kpis) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-heading font-bold text-foreground">Métricas</h1>
        <p className="text-muted-foreground">Cargando datos...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-foreground">Métricas</h1>
          <p className="text-sm text-muted-foreground">KPIs de producto para seguimiento semanal</p>
        </div>
        <Badge variant="outline" className="text-xs">
          {new Date().toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" })}
        </Badge>
      </div>

      {/* Row 1: Users */}
      <div>
        <h2 className="text-sm font-heading font-bold text-muted-foreground uppercase tracking-wide mb-3">Usuarios</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard icon={Users} label="Total registrados" value={kpis.totalUsers} />
          <KpiCard icon={TrendingUp} label="Nuevos esta semana" value={kpis.newUsersThisWeek} />
          <KpiCard icon={Users} label="Activos (7 días)" value={kpis.activeUsers7d} />
          <KpiCard icon={Users} label="Activos (30 días)" value={kpis.activeUsers30d} />
        </div>
      </div>

      {/* Row 2: Conversion & Engagement */}
      <div>
        <h2 className="text-sm font-heading font-bold text-muted-foreground uppercase tracking-wide mb-3">Conversión y engagement</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard icon={Library} label="% que guardó títulos" value={`${kpis.conversionRate}%`} subtitle={`${kpis.usersWithLibrary} de ${kpis.totalUsers} usuarios`} />
          <KpiCard icon={BookOpen} label="Títulos guardados (total)" value={kpis.totalLibraryAdds} />
          <KpiCard icon={Headphones} label="Usuarios con progreso" value={kpis.usersWithProgress} />
          <KpiCard icon={Clock} label="Tiempo total escuchado" value={formatSeconds(kpis.totalListenedSeconds)} subtitle={kpis.avgProgress > 0 ? `Progreso promedio: ${kpis.avgProgress}%` : undefined} />
        </div>
      </div>

      {/* Row 3: Offline & Catalog */}
      <div>
        <h2 className="text-sm font-heading font-bold text-muted-foreground uppercase tracking-wide mb-3">Offline y catálogo</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard icon={Download} label="Descargas offline" value={kpis.totalDownloads} subtitle={`${kpis.uniqueDownloaders} usuarios`} />
          <KpiCard icon={BarChart3} label="Adopción offline" value={`${kpis.offlineAdoption}%`} />
          <KpiCard icon={Library} label="Publicados" value={kpis.publishedContent} subtitle={`de ${kpis.totalContent} total`} />
          <KpiCard icon={Library} label="Borradores pendientes" value={kpis.draftContent} />
        </div>
      </div>

      {/* Row 4: Funnel de paywall */}
      <div>
        <h2 className="text-sm font-heading font-bold text-muted-foreground uppercase tracking-wide mb-3">Funnel de paywall</h2>
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <p className="text-xs text-muted-foreground mb-1">1. Paywall visto</p>
                <p className="text-2xl font-heading font-bold">{kpis.paywallShown}</p>
                <p className="text-xs text-muted-foreground mt-1">Usuarios que llegaron al paywall</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">2. Inició compra</p>
                <p className="text-2xl font-heading font-bold">{kpis.purchaseStart}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  <span className="font-semibold text-foreground">{kpis.startRate}%</span> tap en un plan
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">3. Compra exitosa</p>
                <p className="text-2xl font-heading font-bold text-primary">{kpis.purchaseSuccess}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  <span className="font-semibold text-foreground">{kpis.completeRate}%</span> completó el pago
                </p>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t">
              <p className="text-xs text-muted-foreground">
                Conversión end-to-end (paywall → pago): <span className="font-bold text-foreground">{kpis.overallConvRate}%</span>
              </p>
            </div>
            {kpis.previewsOpened > 0 && (
              <div className="mt-4 pt-4 border-t">
                <p className="text-xs text-muted-foreground mb-1">Preview Reader → Compra</p>
                <p className="text-sm">
                  <span className="font-semibold">{kpis.previewsOpened}</span> previews abiertos ·{" "}
                  <span className="font-semibold">{kpis.previewToPurchase}</span> convirtieron ·{" "}
                  <span className="font-bold text-primary">{kpis.previewConvRate}%</span>
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Charts row */}
      <div className="grid lg:grid-cols-2 gap-6">
        {kpis.regChart.length > 0 && (
          <Card>
            <CardHeader><CardTitle className="text-sm font-heading">Registros últimos 30 días</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={kpis.regChart}>
                  <XAxis dataKey="date" fontSize={11} />
                  <YAxis fontSize={11} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" fill="hsl(160, 45%, 22%)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader><CardTitle className="text-sm font-heading">Títulos más guardados</CardTitle></CardHeader>
          <CardContent>
            {kpis.topContent.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">Aún no hay títulos guardados</p>
            ) : (
              <div className="space-y-3">
                {kpis.topContent.map((item, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                    <span className="text-sm font-medium">{item.title}</span>
                    <Badge variant="secondary">{item.adds} agregado{item.adds !== 1 ? "s" : ""}</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Event analytics */}
      <div>
        <h2 className="text-sm font-heading font-bold text-muted-foreground uppercase tracking-wide mb-3">
          Eventos de producto ({kpis.totalEvents} total)
        </h2>
        <div className="grid lg:grid-cols-2 gap-6">
          {kpis.eventChart.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-sm font-heading">Eventos por tipo</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={Math.max(150, kpis.eventChart.length * 36)}>
                  <BarChart data={kpis.eventChart} layout="vertical">
                    <XAxis type="number" fontSize={11} allowDecimals={false} />
                    <YAxis type="category" dataKey="name" fontSize={11} width={140} />
                    <Tooltip />
                    <Bar dataKey="count" fill="hsl(160, 45%, 32%)" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
          <Card>
            <CardHeader><CardTitle className="text-sm font-heading">Eventos recientes</CardTitle></CardHeader>
            <CardContent>
              {kpis.recentEvents.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">Aún no hay eventos registrados</p>
              ) : (
                <div className="space-y-2 max-h-[320px] overflow-y-auto">
                  {kpis.recentEvents.map((ev, i) => (
                    <div key={i} className="flex items-center justify-between py-1.5 border-b border-border last:border-0">
                      <div>
                        <Badge variant="secondary" className="text-xs">{ev.event_name}</Badge>
                        {ev.properties?.title && <span className="text-xs text-muted-foreground ml-2">{ev.properties.title}</span>}
                      </div>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {new Date(ev.created_at).toLocaleDateString("es-ES", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Empty state guidance */}
      {kpis.totalUsers <= 2 && (
        <Card className="border-dashed">
          <CardContent className="py-8 text-center">
            <p className="text-sm font-heading font-semibold text-foreground mb-2">Datos limitados por ahora</p>
            <p className="text-xs text-muted-foreground max-w-md mx-auto">
              Este tablero se llenará automáticamente cuando los usuarios empiecen a registrarse y consumir contenido.
              Los KPIs están listos para escalar a cientos de usuarios.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, subtitle }: { icon: any; label: string; value: string | number; subtitle?: string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center">
            <Icon className="h-4 w-4 text-primary" />
          </div>
          <p className="text-xs text-muted-foreground">{label}</p>
        </div>
        <p className="text-2xl font-bold text-foreground">{value}</p>
        {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
      </CardContent>
    </Card>
  );
}
